// src/layouts/SidebarLayout/index.tsx
import { FC, ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, Container, useMediaQuery, useTheme } from "@mui/material";
import { Outlet, useLocation } from "react-router-dom";
import { usePullToRefresh } from "src/hooks/usePullToRefresh/usePullToRefresh";
import Swal from "sweetalert2";
import { CHATBOT_XL_PANEL_WIDTH } from "src/utils/chatbotLayout";

import Sidebar from "./Sidebar";
import Toggle from "./Header/Toggle";
import Breadcrumb from "src/components/common/Breadcrumb";
import PageHeader from "src/components/common/PageHeader";
import BottomNavigation from "./BottomNavigation";
import NetworkStatus from "src/components/common/NetworkStatus";
import Logo from "src/components/common/LogoSign";
import Chatbot from "src/components/learning/Chatbot";
import { PullToRefreshIndicator } from "src/components/common/PullToRefreshIndicator/PullToRefreshIndicator";
import SuspenseLoader from "src/components/common/SuspenseLoader";
import { AuthService, logout } from "src/services/auth.service";

/**
 * Kondisi untuk menampilkan Chatbot:
 * - Bukan mobile (isMobile == "false" atau undefined)
 * - URL tidak mengandung path yang dilarang (kecuali beberapa path yang diizinkan secara spesifik)
 *
 * Contoh:
 * - /dashboard di Desktop -> Chatbot Tampil.
 * - /classes/tryout/123 di Desktop -> Chatbot TIDAK Tampil. (Karena URL mengandung /classes/tryout/)
 * - /all-menu di Desktop -> Chatbot TIDAK Tampil. (Karena URL mengandung /all-menu)
 * - /classes/tryout/123/analytic di Desktop dan Mobile -> Chatbot Tampil. (Meskipun mengandung /classes/tryout/, kondisi kedua (||) mengizinkannya secara spesifik)
 */

interface SidebarLayoutProps {
  children?: ReactNode;
}

const SidebarLayout: FC<SidebarLayoutProps> = () => {
  const theme = useTheme();
  const isMobile = localStorage.getItem("isMobile");
  const location = useLocation();
  const hasHandledForbiddenRef = useRef(false);
  const [isCheckingSession, setIsCheckingSession] = useState(false);
  // State chatbot sidebar: default TERTUTUP untuk SEMUA ukuran
  // EKSEPSI: di layar XL (≥1840px, push-content aktif) → default TERBUKA
  // useMediaQuery jalan di client-side, jadi sync lewat useEffect setelah mount
  const [chatbotOpen, setChatbotOpen] = useState<boolean>(false);

  // Layar XL (theme xl = 1840px) — mode push content chatbot aktif
  const isXlUp = useMediaQuery(theme.breakpoints.up('xl')); // theme xl = 1840

  // Sync initial state: buka otomatis HANYA di layar XL (push-content)
  useEffect(() => {
    if (isXlUp) {
      setChatbotOpen(true);
    }
  }, [isXlUp]);

  // Push content hanya di layar XL + chatbot terbuka + mode desktop (bukan HP)
  const pushContent = isXlUp && chatbotOpen && (isMobile === "false" || isMobile == null);

  // Pola URL yang ingin dicocokkan
  const urlPatternEcourse = /^\/classes\/e-course\/\d+(\/\d+){1,3}$/;
  const urlPatternClassroom = /^\/classes\/classroom\/\d+(\/\d+){1,3}$/;

  // Pull to refresh hanya untuk dashboard di mobile
  const shouldEnablePTR = !(isMobile == "false" || isMobile == null);

  const handleRefresh = async () => {
    window.location.reload();
  };

  const getToken = () => {
    const token = localStorage.getItem("TOKEN");
    return token && token !== "null" && token !== "undefined" ? token : null;
  };

  useLayoutEffect(() => {
    const isMobileFlow = !(isMobile == "false" || isMobile == null);

    if (!isMobileFlow) {
      return;
    }

    const token = getToken();

    if (!token) {
      if (!hasHandledForbiddenRef.current) {
        hasHandledForbiddenRef.current = true;
        setIsCheckingSession(false);
        Swal.fire({
          title: "Forbidden",
          text: "Anda tidak memiliki akses ke halaman ini",
          icon: "error",
        }).then(async () => {
          await logout(true);
        });
      }

      return;
    }

    let cancelled = false;
    setIsCheckingSession(true);
    AuthService.me()
      .finally(() => {
        if (!cancelled) {
          setIsCheckingSession(false);
          hasHandledForbiddenRef.current = false;
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isMobile, location.pathname]);

  const { containerRef, isRefreshing, pullDistance, threshold } =
    usePullToRefresh({
      onRefresh: handleRefresh,
      disabled: !shouldEnablePTR
    });

  return (
    <Box
      ref={shouldEnablePTR ? containerRef : null}
      sx={{
        flex: 1,
        height: "100%",
        ...(shouldEnablePTR && {
          height: "100vh",
          overflowY: "auto",
          overflowX: "hidden"
        }),
        ".MuiPageTitle-wrapper": {
          background:
            theme.palette.mode !== "dark"
              ? theme.colors.alpha.trueWhite[5]
              : theme.colors.alpha.white[50],
          marginBottom: `${theme.spacing(4)}`,
        },
      }}
    >
      {isCheckingSession && <SuspenseLoader />}

      {/* Refresh Indicator - Hanya untuk dashboard mobile */}
      {shouldEnablePTR && (
        <PullToRefreshIndicator
          isRefreshing={isRefreshing}
          pullDistance={pullDistance}
          threshold={threshold}
        />
      )}

      {((isMobile == "false" || isMobile == null) ||
        (urlPatternEcourse.test(location.pathname) || urlPatternClassroom.test(location.pathname))) && (
          <>
            <Box
              sx={{
                display: { lg: "none", xs: "flex" },
                alignItems: "center",
                justifyContent: "space-between",
                pr: 2,
              }}
            >
              <Toggle />
              <Box sx={{ mr: -2 }}>
                <Logo scale={0.7} />
              </Box>
            </Box>
            <Sidebar />
          </>
        )}

      <Box
        sx={{
          position: "relative",
          zIndex: 5,
          display: "block",
          flex: 1,
          pt: `${theme.spacing(0)}`,
          pb: { xs: `${theme.spacing(8)}`, lg: `${theme.spacing(2)}` },
          [theme.breakpoints.up("lg")]: (isMobile === "false" || isMobile === null) && {
            // Di layar XL dengan chatbot terbuka, konten di-push ke kiri (tambah margin kanan)
            ml: `${theme.sidebar.width}`,
            mr: pushContent ? `${CHATBOT_XL_PANEL_WIDTH}px` : 0,
            pl: `20px`,
            pt: `${theme.spacing(4)}`,
          },
          // Geser halus konten saat chatbot buka/tutup di layar XL
          transition: 'margin-right 200ms ease, margin-left 200ms ease',
        }}
      >
        <Container maxWidth="lg">
          <NetworkStatus />
          <Box display="block">
            {(isMobile == "false" || isMobile == null) ? (
              <Breadcrumb />
            ) : (
              <div style={{ marginTop: "20px" }}></div>
            )}
            <PageHeader />
            {!isCheckingSession && <Outlet />}
          </Box>
        </Container>
      </Box>

      <Box sx={{ display: { xs: "block", lg: "none" } }}>
        {(isMobile == "false" || isMobile == null) && <BottomNavigation />}
      </Box>

      {
        (
          (
            // Pengecualian: Selalu tampilkan di halaman analitik, baik mobile maupun desktop
            /^\/classes\/tryout\/\d+\/analytic$/.test(location.pathname)
          )
          || // ATAU
          (
            // Aturan umum seperti sebelumnya:
            // Harus di desktop DAN bukan di halaman yang dilarang
            (isMobile == "false" || isMobile == undefined) &&
            !(location.pathname.includes("/classes/tryout/") || location.pathname.includes("/all-menu"))
          )
        )
        && <Chatbot open={chatbotOpen} onToggle={() => setChatbotOpen(prev => !prev)} />
      }
    </Box>
  );
};

export default SidebarLayout;